import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Search, Users, Calendar, Shield, Zap, Globe,
  Heart, MessageCircle, MapPin, CheckCircle2, Send, Sparkles, TrendingUp, Lock,
} from "lucide-react";

const IMG = {
  feedPost: "https://images.unsplash.com/photo-1739550635585-484633b21450?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  stadium: "https://images.unsplash.com/photo-1679391029864-d46f366a456b?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  a1: "https://images.unsplash.com/photo-1653324502559-ae8d4aa4dd57?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
  a2: "https://images.unsplash.com/photo-1690543364186-973ade5dd0c1?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
  a3: "https://images.unsplash.com/photo-1770914701397-74ec4efabbd2?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
  a4: "https://images.unsplash.com/photo-1661345676027-2690108ec331?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
};

/* Reveal-on-scroll wrapper */
function Reveal({ children, className = "", delay = 0, y = 28 }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : `translateY(${y}px)`,
        transition: `opacity 0.7s ease ${delay}ms, transform 0.8s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

/* Browser-like window frame for product previews */
function AppWindow({ children, label = "sportsunity.app", className = "" }) {
  return (
    <div className={`rounded-xl bg-white border border-[#E6E6E6] shadow-[0_24px_60px_-20px_rgba(7,69,114,0.35)] overflow-hidden ${className}`}>
      <div className="h-9 flex items-center gap-2 px-3.5 border-b border-[#EFEFEF] bg-[#FBFBFB]">
        <Lock size={10} className="text-zinc-400 shrink-0" />
        <span className="flex-1 text-[10px] text-zinc-400 bg-white border border-[#EFEFEF] rounded-full px-3 py-0.5 truncate text-center">{label}</span>
      </div>
      <div className="bg-[#FAFAFA]">{children}</div>
    </div>
  );
}

function Ava({ src, ring = "#074572", size = 40 }) {
  return (
    <img
      src={src}
      alt=""
      className="rounded-full object-cover shrink-0"
      style={{ width: size, height: size, boxShadow: `0 0 0 2px #fff, 0 0 0 3.5px ${ring}` }}
    />
  );
}

function StatBar({ label, value, color = "#074572" }) {
  return (
    <div>
      <div className="flex justify-between text-[10px] text-zinc-500 mb-1">
        <span>{label}</span><span className="font-semibold text-zinc-700">{value}</span>
      </div>
      <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${value}%`, background: color }} />
      </div>
    </div>
  );
}

/* ---------- Product preview mockups ---------- */

function FeedPreview() {
  return (
    <AppWindow label="sportsunity.app/feed">
      <div className="p-4 space-y-3">
        <div className="bg-white rounded-lg border border-[#E6E6E6] p-4">
          <div className="flex items-center gap-3">
            <Ava src={IMG.a2} ring="#074572" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-zinc-900">Diego Martín</span>
                <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#074572]/8 text-[#074572] uppercase">Athlete</span>
              </div>
              <span className="text-[11px] text-zinc-400">Forward · 2h ago</span>
            </div>
            <Sparkles size={15} className="text-[#F85037]" />
          </div>
          <p className="text-[13px] text-zinc-700 mt-3 leading-relaxed">
            Two goals and an assist in today's trial match. Grateful for the opportunity — ready for the next step. ⚽️
          </p>
          <img src={IMG.feedPost} alt="" className="mt-3 rounded-md w-full h-36 object-cover" />
          <div className="flex items-center gap-5 mt-3 text-zinc-400">
            <span className="flex items-center gap-1.5 text-[12px]"><Heart size={14} className="text-[#F85037]" fill="#F85037" /> 248</span>
            <span className="flex items-center gap-1.5 text-[12px]"><MessageCircle size={14} /> 32</span>
          </div>
        </div>
      </div>
    </AppWindow>
  );
}

function ScoutPreview() {
  const cards = [
    { img: IMG.a3, name: "Kwame Osei", pos: "Winger", loc: "Accra, GH", pace: 92, stam: 84 },
    { img: IMG.a1, name: "Luca Romano", pos: "Midfielder", loc: "Milan, IT", pace: 78, stam: 90 },
  ];
  return (
    <AppWindow label="sportsunity.app/scout">
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {["All roles", "Forwards", "U-21", "Open to offers"].map((c, i) => (
            <span key={c} className={`text-[10px] px-2.5 py-1 rounded-full border ${i === 1 ? "bg-[#074572] text-white border-[#074572]" : "bg-white text-zinc-500 border-[#E6E6E6]"}`}>{c}</span>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {cards.map((p) => (
            <div key={p.name} className="bg-white rounded-lg border border-[#E6E6E6] p-3">
              <div className="flex items-center gap-2.5">
                <Ava src={p.img} ring="#F85037" size={38} />
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-zinc-900 truncate">{p.name}</p>
                  <p className="text-[10px] text-zinc-400 flex items-center gap-1"><MapPin size={10} /> {p.loc}</p>
                </div>
              </div>
              <span className="inline-block mt-2 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#074572]/8 text-[#074572] uppercase">{p.pos}</span>
              <div className="mt-2.5 space-y-2">
                <StatBar label="Pace" value={p.pace} color="#F85037" />
                <StatBar label="Stamina" value={p.stam} color="#074572" />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-[#074572] font-medium">
          <CheckCircle2 size={13} /> 1,240 profiles match your filters
        </div>
      </div>
    </AppWindow>
  );
}

function ProfilePreview() {
  return (
    <AppWindow label="sportsunity.app/profile">
      <div>
        <div className="px-4 pt-5 pb-4">
          <div className="flex items-center gap-3">
            <Ava src={IMG.a4} ring="#074572" size={52} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-zinc-900">Mateo Álvarez</p>
              <p className="text-[11px] text-zinc-400">Centre-Back · Boca Juniors Youth</p>
            </div>
            <span className="inline-flex items-center gap-1 text-[9px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 shrink-0">
              <Shield size={10} /> Verified
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-4">
            {[["Matches", "48"], ["Clean sheets", "19"], ["Pass %", "91"]].map(([k, v]) => (
              <div key={k} className="bg-white rounded-md border border-[#E6E6E6] p-2 text-center">
                <p className="text-base font-semibold text-[#074572]" style={{ fontFamily: "Outfit" }}>{v}</p>
                <p className="text-[9px] text-zinc-400">{k}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 bg-white rounded-md border border-[#E6E6E6] p-3 space-y-2">
            <p className="text-[10px] font-semibold text-zinc-500 flex items-center gap-1.5"><TrendingUp size={12} className="text-[#F85037]" /> Performance</p>
            <StatBar label="Sprint speed" value={88} color="#F85037" />
            <StatBar label="Vertical jump" value={74} color="#074572" />
            <StatBar label="Endurance" value={82} color="#074572" />
          </div>
        </div>
      </div>
    </AppWindow>
  );
}

function MessagingPreview() {
  return (
    <AppWindow label="sportsunity.app/messages">
      <div className="p-4">
        <div className="flex items-center gap-2.5 pb-3 border-b border-[#EFEFEF]">
          <Ava src={IMG.a1} ring="#074572" size={34} />
          <div>
            <p className="text-[13px] font-semibold text-zinc-900">Coach Anderson</p>
            <p className="text-[10px] text-green-600 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Online</p>
          </div>
        </div>
        <div className="space-y-2.5 py-3">
          <div className="max-w-[78%] bg-white border border-[#E6E6E6] rounded-2xl rounded-tl-sm px-3 py-2 text-[12px] text-zinc-700">
            Impressive highlights! Can you make our trial on Saturday?
          </div>
          <div className="max-w-[78%] ml-auto bg-[#074572] text-white rounded-2xl rounded-tr-sm px-3 py-2 text-[12px]">
            Absolutely — I'll be there. Thank you for the opportunity! 🙌
          </div>
          <div className="flex items-center gap-1 pl-1">
            <span className="w-1.5 h-1.5 rounded-full bg-zinc-300 animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-zinc-300 animate-bounce" style={{ animationDelay: "120ms" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-zinc-300 animate-bounce" style={{ animationDelay: "240ms" }} />
          </div>
        </div>
        <div className="flex items-center gap-2 bg-white border border-[#E6E6E6] rounded-full px-3 py-1.5">
          <span className="text-[12px] text-zinc-300 flex-1">Write a message…</span>
          <span className="w-7 h-7 rounded-full bg-[#F85037] flex items-center justify-center"><Send size={13} className="text-white" /></span>
        </div>
      </div>
    </AppWindow>
  );
}

/* ---------- Inside-the-platform section ---------- */
function InsideRow({ eyebrow, title, desc, bullets, preview, flip = false }) {
  return (
    <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      <Reveal className={flip ? "lg:order-2" : ""}>
        <p className="text-xs font-semibold tracking-widest uppercase text-[#F85037] mb-3">{eyebrow}</p>
        <h3 className="text-2xl sm:text-3xl font-medium text-[#074572] mb-4" style={{ fontFamily: "Outfit" }}>{title}</h3>
        <p className="text-[15px] text-zinc-500 leading-relaxed mb-6">{desc}</p>
        <ul className="space-y-3">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-3 text-sm text-zinc-700">
              <CheckCircle2 size={18} className="text-[#074572] shrink-0 mt-0.5" /> {b}
            </li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={120} className={flip ? "lg:order-1" : ""}>{preview}</Reveal>
    </div>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#E6E6E6]" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-white flex items-center justify-center p-1">
              <img src="/logo.png" alt="SportsUnity" className="w-full h-full object-contain" />
            </div>
            <span className="text-lg font-semibold text-[#074572]" style={{ fontFamily: "Outfit" }}>SportsUnity</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-500">
            <a href="#inside" className="hover:text-[#074572] transition-colors">Platform</a>
            <a href="#features" className="hover:text-[#074572] transition-colors">Features</a>
            <a href="#join" className="hover:text-[#074572] transition-colors">Join</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-[#074572] font-medium" data-testid="landing-login-btn">Log In</Button>
            </Link>
            <Link to="/signup">
              <Button className="bg-[#F85037] hover:bg-[#e04530] text-white font-medium rounded-md px-5" data-testid="landing-signup-btn">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-40 lg:pt-44 lg:pb-56 px-4 sm:px-6 lg:px-8 overflow-hidden" data-testid="landing-hero">
        <div className="absolute inset-0 z-0">
          <video autoPlay muted loop playsInline preload="metadata" className="w-full h-full object-cover" data-testid="hero-bg-video">
            <source src="https://customer-assets.emergentagent.com/job_sportsunity-pro/artifacts/ffzzljys_202749-918944227.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-br from-[#074572]/90 via-[#053a60]/80 to-black/75" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#FAFAFA]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-md border border-white/15 rounded-full mb-6 animate-fade-up">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F85037]" />
            <span className="text-xs font-semibold tracking-wide uppercase text-white">The home of football talent</span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] font-medium text-white mb-6 animate-fade-up stagger-1 drop-shadow-lg" style={{ fontFamily: "Outfit" }}>
            Where football<br />careers take off
          </h1>
          <p className="text-base sm:text-lg text-white/85 leading-relaxed max-w-2xl mx-auto mb-10 animate-fade-up stagger-2">
            Athletes, scouts and coaches, together on one platform. Showcase your talent, discover the next star, and build the connections that move careers forward.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-up stagger-3">
            <Link to="/signup">
              <Button className="bg-[#F85037] hover:bg-[#e04530] text-white font-medium rounded-md px-8 py-3 text-base h-auto shadow-lg" data-testid="hero-cta-btn">
                Join SportsUnity <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
            <a href="#inside">
              <Button variant="outline" className="bg-white/10 backdrop-blur-md border-white/30 text-white font-medium rounded-md px-8 py-3 text-base h-auto hover:bg-white/20" data-testid="hero-learn-btn">
                See it in action
              </Button>
            </a>
          </div>
        </div>

        {/* Floating product preview overlapping the next section */}
        <div className="relative z-10 max-w-2xl mx-auto mt-14 -mb-56 px-2 animate-fade-up stagger-3">
          <FeedPreview />
        </div>
      </section>

      {/* Stats strip */}
      <section className="pt-64 lg:pt-72 pb-12 bg-white border-b border-[#E6E6E6]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { value: "Athletes", sub: "showcasing talent" },
            { value: "Scouts", sub: "finding the next star" },
            { value: "Coaches", sub: "building squads" },
            { value: "50+", sub: "countries connected" },
          ].map((stat) => (
            <Reveal key={stat.value}>
              <p className="text-xl font-semibold text-[#074572]" style={{ fontFamily: "Outfit" }}>{stat.value}</p>
              <p className="text-xs text-zinc-400 mt-1">{stat.sub}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Inside the platform */}
      <section id="inside" className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-16 lg:mb-24">
            <p className="text-xs font-semibold tracking-widest uppercase text-[#F85037] mb-3">A look inside</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-[#074572] mb-4" style={{ fontFamily: "Outfit" }}>
              See what the platform feels like
            </h2>
            <p className="text-base text-zinc-500 max-w-xl mx-auto">
              Scroll through a real feel of SportsUnity — the feed, talent discovery, pro profiles and messaging.
            </p>
          </Reveal>

          <div className="space-y-24 lg:space-y-36">
            <InsideRow
              eyebrow="Scout Navigator"
              title="Discover talent in seconds"
              desc="Filter the entire network by role, position, age, country, profession and live performance data. Shortlist players and reach out — all in one place."
              bullets={["Smart filters & saved searches", "Performance-backed profiles", "Recruitment funnel with stage notes"]}
              preview={<ScoutPreview />}
            />
            <InsideRow
              flip
              eyebrow="Your network feed"
              title="Share your journey, get noticed"
              desc="Post updates, highlights and YouTube reels. Every match report and milestone builds your reputation in front of the people who matter."
              bullets={["Photo & YouTube posts", "Likes, comments & connections", "Stay on scouts' and coaches' radar"]}
              preview={<FeedPreview />}
            />
            <InsideRow
              eyebrow="Pro profiles"
              title="A profile that proves it"
              desc="Show your stats, verified diplomas and performance metrics in a clean, professional profile that speaks louder than a CV."
              bullets={["Verified athletes, scouts & coaches", "Performance & physical metrics", "Certificates and licence verification"]}
              preview={<ProfilePreview />}
            />
            <InsideRow
              flip
              eyebrow="Real-time messaging"
              title="Start the right conversations"
              desc="Message your network directly with read receipts and typing indicators — with built-in guardian approvals that keep young athletes safe."
              bullets={["Live chat with typing indicators", "Parent approval for minors", "Secure and professional"]}
              preview={<MessagingPreview />}
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 bg-white border-y border-[#E6E6E6]">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-[#074572] mb-4" style={{ fontFamily: "Outfit" }}>
              Everything you need
            </h2>
            <p className="text-base text-zinc-500 max-w-xl mx-auto">
              Built for the football community — every feature designed to help you connect, grow and succeed.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Search, title: "Scout Navigator", desc: "Discover talent with advanced filters by position, location, age and skills." },
              { icon: Users, title: "Professional Network", desc: "Build meaningful connections with athletes, scouts and coaches worldwide." },
              { icon: Zap, title: "Real-Time Messaging", desc: "Communicate directly with your network. Quick, secure and professional." },
              { icon: Calendar, title: "Events & Trials", desc: "Find scouting days, tournaments, workshops and networking events near you." },
              { icon: Globe, title: "Global Reach", desc: "Access talent and opportunities across 50+ countries and major leagues." },
              { icon: Shield, title: "Verified Profiles", desc: "Trust the platform — verified athletes, licensed scouts and certified coaches." },
            ].map((feature, i) => (
              <Reveal key={feature.title} delay={(i % 3) * 80}>
                <div className="h-full p-6 bg-[#FAFAFA] rounded-xl border border-[#E6E6E6] hover:shadow-md hover:-translate-y-1 transition-all duration-300" data-testid={`feature-${feature.title.toLowerCase().replace(/\s/g, "-")}`}>
                  <div className="w-10 h-10 rounded-md bg-[#074572]/5 flex items-center justify-center mb-4">
                    <feature.icon size={20} className="text-[#074572]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-medium text-zinc-900 mb-2" style={{ fontFamily: "Outfit" }}>{feature.title}</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed">{feature.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="join" className="py-24 bg-[#074572] relative overflow-hidden" data-testid="landing-cta">
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#F85037]/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-16 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
        <Reveal className="relative max-w-3xl mx-auto text-center px-4">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-white mb-4" style={{ fontFamily: "Outfit" }}>
            Ready to join the network?
          </h2>
          <p className="text-base text-white/70 mb-8">
            Create your profile today and connect with the football community.
          </p>
          <Link to="/signup">
            <Button className="bg-[#F85037] hover:bg-[#e04530] text-white font-medium rounded-md px-8 py-3 text-base h-auto shadow-lg" data-testid="cta-signup-btn">
              Create Free Account <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </Reveal>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-[#E6E6E6] bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded bg-white border border-[#E6E6E6] flex items-center justify-center p-0.5">
              <img src="/logo.png" alt="SportsUnity" className="w-full h-full object-contain" />
            </div>
            <span className="text-sm text-zinc-500">SportsUnity. The Professional Network for Football.</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-zinc-500">
            <Link to="/privacy" className="hover:text-[#074572] transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-[#074572] transition-colors">Terms</Link>
            <Link to="/support" className="hover:text-[#074572] transition-colors">Support</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
